import { InputType } from "~/scad-shared/types/generic";
import type { IFieldConfig } from "../types/scad";
import { controlTypeOptions, defaultColSpan, fullWidthColSpan } from "./defaults";
import { getActionFieldOptions } from './metaActionConfig';

export const metaColumnFieldConfig: IFieldConfig[] = [
  // Basic Info Group
  {
    name: "ColumnName",
    label: "Column Name (DB)",
    inputType: InputType.InputText,
    fieldGroup: "Basic Info",
    colSpan: defaultColSpan,
    disabled: true,
  },
  {
    name: "ColumnNamePc",
    label: "Column Name (Pascal Case)",
    inputType: InputType.InputText,
    fieldGroup: "Basic Info",
    colSpan: defaultColSpan,
    disabled: true,
  },
  {
    name: "ColumnLabel",
    label: "Display Label",
    inputType: InputType.InputText,
    fieldGroup: "Basic Info",
    colSpan: defaultColSpan,
    required: true,
    validation: ["required"]
  },
  {
    name: "ColumnAlias",
    label: "Column Alias",
    inputType: InputType.InputText,
    fieldGroup: "Basic Info",
    colSpan: defaultColSpan,
  },
  {
    name: "DataType",
    label: "Data Type",
    inputType: InputType.InputText,
    fieldGroup: "Basic Info",
    colSpan: defaultColSpan,
    disabled: true,
  },
  {
    name: "Help",
    label: "Help Text",
    inputType: InputType.InputText,
    fieldGroup: "Basic Info",
    colSpan: fullWidthColSpan,
  },

  // Control Settings Group
  {
    name: "ControlType",
    label: "Control Type",
    inputType: InputType.SelectDropdown,
    fieldGroup: "Control Settings",
    colSpan: defaultColSpan,
    required: true,
    validation: ["required"]
  },
  {
    name: "FilterControlType",
    label: "Filter Control Type",
    inputType: InputType.SelectDropdown,
    fieldGroup: "Control Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "Placeholder",
    label: "Placeholder",
    inputType: InputType.InputText,
    fieldGroup: "Control Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "SearchType",
    label: "Search Type",
    inputType: InputType.SelectDropdown,
    fieldGroup: "Control Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "DateFormat",
    label: "Date Format",
    inputType: InputType.InputText,
    fieldGroup: "Control Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "GridColumnWidth",
    label: "Grid Column Width",
    inputType: InputType.InputText,
    fieldGroup: "Control Settings",
    colSpan: defaultColSpan,
  },

  // Visibility Group
  {
    name: "IsEnabled",
    label: "Enabled",
    inputType: InputType.BoolSelect,
    fieldGroup: "Visibility",
    colSpan: defaultColSpan,
  },
  {
    name: "ShowInGrid",
    label: "Show in Grid",
    inputType: InputType.BoolSelect,
    fieldGroup: "Visibility",
    colSpan: defaultColSpan,
  },
  {
    name: "ShowInCreateForm",
    label: "Show in Create Form",
    inputType: InputType.BoolSelect,
    fieldGroup: "Visibility",
    colSpan: defaultColSpan,
  },
  {
    name: "ShowInEditForm",
    label: "Show in Edit Form",
    inputType: InputType.BoolSelect,
    fieldGroup: "Visibility",
    colSpan: defaultColSpan,
  },
  {
    name: "ShowInFilter",
    label: "Show in Filter",
    inputType: InputType.BoolSelect,
    fieldGroup: "Visibility",
    colSpan: defaultColSpan,
  },
  {
    name: "Mutable",
    label: "Mutable (Editable)",
    inputType: InputType.BoolSelect,
    fieldGroup: "Visibility",
    colSpan: defaultColSpan,
  },
  {
    name: "IsIdentity",
    label: "Is Identity",
    inputType: InputType.BoolSelect,
    fieldGroup: "Visibility",
    colSpan: defaultColSpan,
    disabled: true,
  },
  {
    name: "Computed",
    label: "Computed",
    inputType: InputType.BoolSelect,
    fieldGroup: "Visibility",
    colSpan: defaultColSpan,
  },
  {
    name: "IncludeInCopy",
    label: "Include in Copy",
    inputType: InputType.BoolSelect,
    fieldGroup: "Visibility",
    colSpan: defaultColSpan,
  },
  {
    name: "ShowAllOptionInSelect",
    label: "Show 'All' Option",
    inputType: InputType.BoolSelect,
    fieldGroup: "Visibility",
    colSpan: defaultColSpan,
  },

  // Order Group
  {
    name: "GridOrder",
    label: "Grid Order",
    inputType: InputType.IntegerInput,
    fieldGroup: "Order",
    colSpan: defaultColSpan,
  },
  {
    name: "EditFormOrder",
    label: "Edit Form Order",
    inputType: InputType.IntegerInput,
    fieldGroup: "Order",
    colSpan: defaultColSpan,
  },
  {
    name: "CreateFormOrder",
    label: "Create Form Order",
    inputType: InputType.IntegerInput,
    fieldGroup: "Order",
    colSpan: defaultColSpan,
  },
  {
    name: "FilterOrder",
    label: "Filter Order",
    inputType: InputType.IntegerInput,
    fieldGroup: "Order",
    colSpan: defaultColSpan,
  },

  // Column Layout Group
  {
    name: "ColSpanSm",
    label: "Col Span SM",
    inputType: InputType.IntegerInput,
    fieldGroup: "Column Layout",
    colSpan: defaultColSpan,
  },
  {
    name: "ColSpanMd",
    label: "Col Span MD",
    inputType: InputType.IntegerInput,
    fieldGroup: "Column Layout",
    colSpan: defaultColSpan,
  },
  {
    name: "ColSpanLg",
    label: "Col Span LG",
    inputType: InputType.IntegerInput,
    fieldGroup: "Column Layout",
    colSpan: defaultColSpan,
  },
  {
    name: "ColSpanXl",
    label: "Col Span XL",
    inputType: InputType.IntegerInput,
    fieldGroup: "Column Layout",
    colSpan: defaultColSpan,
  },
  {
    name: "ColSpanXxl",
    label: "Col Span XXL",
    inputType: InputType.IntegerInput,
    fieldGroup: "Column Layout",
    colSpan: defaultColSpan,
  },
  {
    name: "FieldGroup",
    label: "Field Group",
    inputType: InputType.InputText,
    fieldGroup: "Column Layout",
    colSpan: defaultColSpan,
  },
  {
    name: "FormSection",
    label: "Form Section",
    inputType: InputType.InputText,
    fieldGroup: "Column Layout",
    colSpan: defaultColSpan,
  },

  // Foreign Key Settings Group
  {
    name: "FkTableSchema",
    label: "FK Table Schema",
    inputType: InputType.InputText,
    fieldGroup: "Foreign Key Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "FkTableName",
    label: "FK Table Name",
    inputType: InputType.InputText,
    fieldGroup: "Foreign Key Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "DisplayColumnName",
    label: "Display Column Name",
    inputType: InputType.InputText,
    fieldGroup: "Foreign Key Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "ValueColumnName",
    label: "Value Column Name",
    inputType: InputType.InputText,
    fieldGroup: "Foreign Key Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "Endpoint",
    label: "Endpoint ID",
    inputType: InputType.InputText,
    fieldGroup: "Foreign Key Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "EndpointRoute",
    label: "Endpoint Route",
    inputType: InputType.InputText,
    fieldGroup: "Foreign Key Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "CascadeFrom",
    label: "Cascade From",
    inputType: InputType.InputText,
    fieldGroup: "Foreign Key Settings",
    colSpan: defaultColSpan,
    help: "Comma-separated list of fields to cascade from",
  },
  {
    name: "ApplyCascadeOnLookupProperty",
    label: "Apply Cascade On Property",
    inputType: InputType.InputText,
    fieldGroup: "Foreign Key Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "FilterDropdown",
    label: "Filter Dropdown",
    inputType: InputType.IntegerInput,
    fieldGroup: "Foreign Key Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "FilterRecordsPerPage",
    label: "Filter Records Per Page",
    inputType: InputType.IntegerInput,
    fieldGroup: "Foreign Key Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "CacheDropdown",
    label: "Cache Dropdown",
    inputType: InputType.IntegerInput,
    fieldGroup: "Foreign Key Settings",
    colSpan: defaultColSpan,
  },

  // Validation Group
  {
    name: "Validation",
    label: "Validation Rules",
    inputType: InputType.InputText,
    fieldGroup: "Validation",
    colSpan: fullWidthColSpan,
    help: "Array of validation rules, e.g., [required], [min, 5]",
  },
  {
    name: "OptionalValidation",
    label: "Optional Validation",
    inputType: InputType.InputText,
    fieldGroup: "Validation",
    colSpan: fullWidthColSpan,
  },

  // Expressions Group
  {
    name: "ShowExpression",
    label: "Show Expression",
    inputType: InputType.InputText,
    fieldGroup: "Expressions",
    colSpan: fullWidthColSpan,
    help: "Expression to determine visibility, e.g., Status == 'Active'",
  },
  {
    name: "DisabledExpression",
    label: "Disabled Expression",
    inputType: InputType.InputText,
    fieldGroup: "Expressions",
    colSpan: fullWidthColSpan,
    help: "Expression to determine if field is disabled",
  },
  {
    name: "DefaultExpression",
    label: "Default Expression",
    inputType: InputType.InputText,
    fieldGroup: "Expressions",
    colSpan: fullWidthColSpan,
    help: "Expression for default value on create",
  },
  {
    name: "ChangeCallback",
    label: "Change Callback",
    inputType: InputType.InputText,
    fieldGroup: "Expressions",
    colSpan: fullWidthColSpan,
    help: "JavaScript function to execute on change",
  },
  {
    name: "ChangeCallbackContextExpression",
    label: "Change Callback Context",
    inputType: InputType.InputText,
    fieldGroup: "Expressions",
    colSpan: fullWidthColSpan,
  },
  {
    name: "SelectSnippet",
    label: "Select Snippet",
    inputType: InputType.InputText,
    fieldGroup: "Expressions",
    colSpan: fullWidthColSpan,
  },

  // Advanced Group
  {
    name: "RouteTo",
    label: "Route To",
    inputType: InputType.InputText,
    fieldGroup: "Advanced",
    colSpan: defaultColSpan,
  },
  {
    name: "MetaActionId",
    label: "Meta Action ID",
    inputType: InputType.IntegerInput,
    fieldGroup: "Advanced",
    colSpan: defaultColSpan,
  },
  {
    name: "Notes",
    label: "Notes",
    inputType: InputType.InputText,
    fieldGroup: "Advanced",
    colSpan: fullWidthColSpan,
  },
];

// Group fields by fieldGroup for easier rendering
export function getMetaColumnFieldsByGroup(): Map<string, IFieldConfig[]> {
  const groups = new Map<string, IFieldConfig[]>();

  metaColumnFieldConfig.forEach((field) => {
    const groupName = field.fieldGroup || "Other";
    if (!groups.has(groupName)) {
      groups.set(groupName, []);
    }
    groups.get(groupName)!.push(field);
  });

  return groups;
}